import React from "react";
import {
    ClinicianCredential,
    IAvailabilitySchedule,
    IClinician,
    RecurrenceRule,
} from "intake_shared/src/types";
import moment from "../../../../utils/app_moment";
import CustomDateSelector from "../../bits/CustomDateSelector/CustomDateSelector";
import { useToasts } from "react-toast-notifications";
import { CalendarId } from "../../../../constants";
import { Dialog } from "../../bits/Dialog/Dialog";
import { Select } from "../../bits/Select/Select";
import {
    findAvailabilityConflicts,
    getAvailabilityRecurrenceOptions,
} from "../../../v2utils";
import { CacheContext } from "../../../../contexts/CacheContext";

interface EditScheduleDialogProps {
    schedules: IAvailabilitySchedule[];
    schedule: IAvailabilitySchedule | null;
    onUpdateSchedule: (schedule: IAvailabilitySchedule) => void;
    setScheduleToEdit: (
        value: React.SetStateAction<IAvailabilitySchedule | null>
    ) => void;
    loggedInClinician: IClinician | undefined;
}

interface RecurrenceOption {
    value: RecurrenceRule;
    label: string;
    isDisabled?: boolean;
}

export default function EditScheduleDialog({
    schedules,
    schedule,
    onUpdateSchedule,
    setScheduleToEdit,
    loggedInClinician,
}: EditScheduleDialogProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const requestedServices = olc?.requestedServices ?? {};

    if (!schedule) {
        return <></>;
    }

    // Don't show EditScheduleDialog for intake sessions - they use AddIntakeSessionDialog instead
    if (
        (schedule as any).isIntakeSession ||
        schedule.calendarId === CalendarId.INTAKE_SESSION_AVAILABILITY
    ) {
        return <></>;
    }

    const handleCancel = () => setScheduleToEdit(null);
    const confirmedBookingSchedules = schedules.filter(
        (item) => item.calendarId === CalendarId.CONFIRMED_BOOKINGS
    );
    const heldBookingSchedules = schedules.filter(
        (item) => item.calendarId === CalendarId.HELD_BOOKINGS
    );

    const availabilitySchedules = schedules.filter(
        (item) => item.calendarId === CalendarId.AVAILABILITY
    );

    const hasConflictingAvailability = findAvailabilityConflicts(
        availabilitySchedules,
        heldBookingSchedules,
        confirmedBookingSchedules,
        schedule
    );

    const handleConfirm = () => {
        if (hasConflictingAvailability) {
            addToast(
                "Duplicate availability window or conflicts with an existing appointment",
                {
                    appearance: "error",
                }
            );
            handleCancel();
        } else {
            onUpdateSchedule(schedule);
            handleCancel();
        }
    };

    const startDateMoment = moment(schedule.starting_date || schedule.start);
    const endingDateMoment = schedule.ending_date
        ? moment(schedule.ending_date)
        : null;
    const allowAnyServiceOneOff =
        loggedInClinician?.departments?.includes("CHP") ?? false;
    const showEndingDate =
        schedule.calendarId !== CalendarId.AVAILABILITY ||
        loggedInClinician?.credential === ClinicianCredential.INTERN;

    const startMoment = moment(schedule.start);
    const endMoment = moment(schedule.end);

    const recurrenceOptions: RecurrenceOption[] = getAvailabilityRecurrenceOptions(
        schedule.calendarId === CalendarId.CONFIRMED_BOOKINGS
            ? undefined
            : schedule.availableService,
        requestedServices,
        allowAnyServiceOneOff
    );

    if (schedule.calendarId === CalendarId.CONFIRMED_BOOKINGS) {
        recurrenceOptions.push(
            { value: RecurrenceRule.TRIWEEKLY, label: "Every 3 weeks" },
            { value: RecurrenceRule.MONTHLY, label: "Monthly" },
            { value: RecurrenceRule.TWO_MONTHS, label: "Every 2 months" },
            { value: RecurrenceRule.THREE_MONTHS, label: "Every 3 months" }
        );

        if (loggedInClinician?.departments?.includes("CHP")) {
            recurrenceOptions.push({
                value: RecurrenceRule.ONE_OFF,
                label: "One-Off",
            });
        }
    } else if (
        schedule.recurrenceRule &&
        !recurrenceOptions.some((option) => option.value === schedule.recurrenceRule)
    ) {
        recurrenceOptions.push({
            value: schedule.recurrenceRule,
            label: `${schedule.recurrenceRule} (Existing)`,
            isDisabled: true,
        });
    }

    return (
        <Dialog
            open={Boolean(schedule)}
            onClose={handleCancel}
            title="Edit date and time"
            onSave={handleConfirm}
            onCancel={handleCancel}
            saveText="Update"
            cancelText="Cancel"
        >
            <div className="v2-form">
                <CustomDateSelector
                    showStartingDate={true}
                    showEndingDate={showEndingDate}
                    defaultDate={{
                        day: startMoment.day(),
                        fromTime: startMoment.format("HH:mm"),
                        toTime: endMoment.format("HH:mm"),
                        startingDate: startDateMoment,
                        endingDate: endingDateMoment,
                    }}
                    onChange={(from, to, endDate) => {
                        setScheduleToEdit({
                            ...schedule,
                            start: from.toISOString(),
                            end: to.toISOString(),
                            starting_date: from.toISOString(),
                            ending_date: endDate ? endDate.toISOString() : "",
                        });
                    }}
                />
                <Select
                    label="Recurring"
                    value={recurrenceOptions.find(
                        (option) => option.value === schedule.recurrenceRule
                    )}
                    onChange={(option) => {
                        if (
                            option &&
                            typeof option === "object" &&
                            "value" in option
                        ) {
                            setScheduleToEdit({
                                ...schedule,
                                recurrenceRule: option.value as RecurrenceRule,
                            });
                        }
                    }}
                    options={recurrenceOptions}
                    isLongListInDialog
                />
            </div>
        </Dialog>
    );
}
